import { useEffect, useMemo, type ReactNode } from 'react';
import { Navigate, Outlet, useLocation, useParams } from 'react-router';
import { useMe } from '../hooks/useMe';
import { WorkspaceContext, useWorkspaceQuery } from '../hooks/useWorkspace';
import { isApiError, errorMessage } from '../lib/api';
import { clearReadOnly } from '../lib/readOnly';
import { STORAGE_KEYS, writeStorage } from '../lib/storage';
import { Alert, Button, ButtonLink, PageLoader } from '../components/ui';
import { PlainLayout } from './layout/PlainLayout';

/** Signed-in users only; others go to /sign-in?next=<here>. */
export function RequireAuth({ children }: { children?: ReactNode }) {
  const me = useMe();
  const location = useLocation();
  if (me.isPending) return <PageLoader label="Checking you are signed in" />;
  if (me.isError) {
    return (
      <PlainLayout>
        <Alert
          tone="danger"
          title="We couldn't reach Seventide"
          action={
            <Button variant="secondary" onClick={() => void me.refetch()}>
              Try again
            </Button>
          }
        >
          {errorMessage(me.error)}
        </Alert>
      </PlainLayout>
    );
  }
  if (!me.data) {
    const next = `${location.pathname}${location.search}`;
    return <Navigate to={`/sign-in${next && next !== '/' ? `?next=${encodeURIComponent(next)}` : ''}`} replace />;
  }
  return <>{children ?? <Outlet />}</>;
}

/** Platform administrators only (the /admin area). */
export function RequirePlatformAdmin({ children }: { children?: ReactNode }) {
  const me = useMe();
  if (!me.data?.user.isPlatformAdmin) {
    return (
      <PlainLayout>
        <Alert tone="warning" title="You don't have access to this page">
          The admin area is only for Seventide staff.
        </Alert>
      </PlainLayout>
    );
  }
  return <>{children ?? <Outlet />}</>;
}

/**
 * Loads `/api/w/:wsId` and provides it to `useWorkspace()` for everything under /w/:wsId.
 * Also remembers the workspace as the last one used.
 */
export function RequireWorkspace({ children }: { children?: ReactNode }) {
  const { wsId } = useParams();
  const query = useWorkspaceQuery(wsId);

  useEffect(() => {
    // A new workspace starts with a clean read-only flag; its own subscription state decides.
    clearReadOnly();
    if (wsId) writeStorage(STORAGE_KEYS.lastWorkspace, wsId);
  }, [wsId]);

  const value = useMemo(
    () => (wsId && query.data ? { wsId, workspace: query.data } : null),
    [wsId, query.data],
  );

  if (!wsId) return <Navigate to="/" replace />;
  if (query.isPending) return <PageLoader label="Opening the family workspace" />;
  if (query.isError || !value) {
    const notFound = isApiError(query.error) && (query.error.status === 404 || query.error.status === 403);
    return (
      <PlainLayout>
        <Alert
          tone={notFound ? 'warning' : 'danger'}
          title={notFound ? "We couldn't find that family workspace" : "We couldn't open this workspace"}
          action={
            <>
              {!notFound && (
                <Button variant="secondary" onClick={() => void query.refetch()}>
                  Try again
                </Button>
              )}
              <ButtonLink to="/" variant="secondary">
                Go to my workspaces
              </ButtonLink>
            </>
          }
        >
          {notFound
            ? 'It may have been removed, or you may no longer be a member. Ask the owner to invite you again.'
            : errorMessage(query.error)}
        </Alert>
      </PlainLayout>
    );
  }
  return <WorkspaceContext.Provider value={value}>{children ?? <Outlet />}</WorkspaceContext.Provider>;
}
